import I18n from 'I18n';
import { useRef, useState } from 'react';
import { LayoutType } from 'signup-components/types';
import { Step } from 'signup-constants/Step';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { HubletToImageUrl } from '../hublets/hubletSelectionUtils';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getFlowKey } from '../routing/getFlowKey';
import { isTransitionAndAnimationsDisabled } from '../selenium/seleniumFeatures';
import { useSignupData } from '../signup-data/useSignupData';
import { ButtonsGridButton, ButtonsGridButtonHolder, ButtonsGridStyled, HubletIcon, StyledButtonText, getButtonColumnCount } from '../styles/ButtonsGrid.styles';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';
import { isUseCaseButtonDisabled } from '../views/UseCaseView';
import { useTracker } from '../tracking/useTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultGetButtonText = ({
  dataKey,
  key
}) => {
  return I18n.text(`views.${dataKey}.options.${key}`);
};
export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  nextButtonLabel,
  shouldDisableAutofocus = false,
  getButtonDescriptionText,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false,
  shouldCenter = true
}) => {
  const gridRef = useRef(null);
  const [clickedNextButton, setClickedNextButon] = useState(false);
  const flowKey = getFlowKey();
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const {
    isAssignedToVariant: isACQ142Variant
  } = useExperiment(ExperimentKeys.ACQ0142, TrackWhen.never);
  const {
    isAssignedToVariant: isACQ0143Variant
  } = useExperiment(ExperimentKeys.ACQ0143, TrackWhen.never);
  const {
    currentStep
  } = useCurrentStep();
  const isHubletSelectionStep = currentStep === Step.HubletSelection;
  const {
    isEmbedded
  } = useSignupEmbedder();
  const {
    isMobile
  } = useResponsiveness();
  const {
    layoutType
  } = useLayoutTemplate();
  const Tracker = useTracker();
  const isFramedCentredStepLayout = layoutType === LayoutType.FramedCentredStep;
  const actualColumnCount = columnCount || preferredColumnCount || getButtonColumnCount(currentStep, isMobile, isEmbedded, isFramedCentredStepLayout);
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const onSelectedChange = value => {
    setClickedNextButon(true);
    onSelect(value);
    submit();
  };
  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);
  const onClickOption = key => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }
    if (!hasNextButton) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      Tracker.signupInteraction(`highlight-${dataKey}`, {
        selectedOption: [key]
      });
    }
  };
  const nthChild = actualColumnCount % 2 === 0 ? 'odd' : 'even';
  const buttonHeight = isACQ0143Variant() ? '72px' : optionHeight;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ButtonsGridStyled
    // @ts-expect-error Expression produces a union type that is too complex to represent.
    , {
      className: "buttonsGrid",
      columnCount: columnCount,
      currentStep: currentStep,
      flowKey: flowKey,
      isFramedCentredStep: isFramedCentredStep,
      isFramedCentredStepLayout: isFramedCentredStepLayout,
      layoutType: layoutType,
      nthChild: nthChild,
      offset: actualColumnCount - 1,
      ref: gridRef,
      shouldCenter: shouldCenter && options.length % actualColumnCount !== 0,
      children: options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder, {
        minHeight: buttonHeight,
        children: /*#__PURE__*/_jsx(ButtonsGridButton, {
          block: true,
          className: "ButtonsGrid_Button",
          currentStep: currentStep,
          "data-selenium": `${dataKey}-${index}`,
          "data-selenium-option": key,
          description: getButtonDescriptionText && getButtonDescriptionText({
            dataKey,
            key,
            index
          }),
          disable: isTransitionAndAnimationsDisabled(),
          disabled: currentStep === Step.UseCase ? isUseCaseButtonDisabled(data, key) : false,
          flowKey: flowKey,
          isFramedCentredStep: isFramedCentredStep,
          name: 'selectable-button',
          selected: data === key || isSelected(key),
          tabIndex: index,
          title: /*#__PURE__*/_jsx(StyledButtonText, {
            className: "m-all-0",
            isACQ0143Variant: isACQ0143Variant(),
            isHubletButtonText: isHubletSelectionStep,
            type: type,
            children: getButtonText({
              dataKey,
              key,
              index
            })
          }),
          truncatable: false,
          type: type,
          updateFontWeight: isACQ142Variant() && isMobile,
          onClick: () => onClickOption(key),
          children: isACQ0143Variant() && isHubletSelectionStep && /*#__PURE__*/_jsx(HubletIcon, {
            responsive: true,
            src: HubletToImageUrl[key]
          })
        })
      }, key))
    }), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      label: nextButtonLabel,
      loading: clickedNextButton,
      onClick: () => onSelectedChange(data)
    })]
  });
};